import React, { useMemo } from "react";
import { AlertTriangle, Clock3, Target } from "lucide-react";
import { PartnerLead, SERVICE_LABELS, parsePortalDate } from "@/types/partnerPortal";
import LeadQuickActions from "./LeadQuickActions";

interface TodaysFocusProps {
  leads: PartnerLead[];
  partnerName?: string;
  onOpenLead?: (leadId: string) => void;
}

const TodaysFocus: React.FC<TodaysFocusProps> = ({ leads, partnerName, onOpenLead }) => {
  const focusLeads = useMemo(() => {
    const now = Date.now();
    return leads
      .filter((lead) => lead.stage !== "έκλεισεΗΔουλειά" && lead.stage !== "ήθελεΠληροφορίες")
      .map((lead) => {
        const ageHours = Math.max(0, (now - parsePortalDate(lead.createdAt).getTime()) / 3_600_000);
        let score = lead.stage === "Νέο" ? 60 : 0;
        if (!lead.linkOpened) score += 40;
        if (lead.timeframe === "άμεσα") score += 40;
        else if (lead.timeframe === "εντός3μηνών") score += 20;
        if (ageHours < 2) score += 30;
        else if (ageHours < 24) score += 15;
        else if (ageHours < 72) score += 10;
        return { lead, score, ageHours };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 4);
  }, [leads]);

  if (focusLeads.length === 0) return null;

  return (
    <section className="overflow-hidden rounded-2xl border border-amber-200 bg-amber-50/60">
      <div className="flex items-start justify-between gap-3 border-b border-amber-200 px-4 py-4 sm:px-5">
        <div>
          <div className="flex items-center gap-2 text-amber-900">
            <Target className="h-5 w-5" />
            <h2 className="font-bold">Προτεραιότητα σήμερα</h2>
          </div>
          <p className="mt-1 text-xs leading-5 text-amber-800/75">Ξεκινήστε από αυτές τις επαφές.</p>
        </div>
        <span className="rounded-full bg-amber-200 px-2.5 py-1 text-xs font-bold text-amber-900">{focusLeads.length}</span>
      </div>
      <div className="divide-y divide-amber-100 bg-white">
        {focusLeads.map(({ lead, ageHours }) => {
          const ageLabel = ageHours < 1
            ? `${Math.max(1, Math.floor(ageHours * 60))} λεπτά`
            : ageHours < 24
              ? `${Math.floor(ageHours)} ${Math.floor(ageHours) === 1 ? "ώρα" : "ώρες"}`
              : `${Math.floor(ageHours / 24)} ${Math.floor(ageHours / 24) === 1 ? "ημέρα" : "ημέρες"}`;
          return (
            <div key={lead.id} className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:px-5">
              <button type="button" onClick={() => onOpenLead?.(lead.id)} className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate text-sm font-bold text-slate-950">{lead.name}</p>
                  {lead.timeframe === "άμεσα" && <span className="rounded-md bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-700">ΑΜΕΣΑ</span>}
                  {!lead.linkOpened && (
                    <span className="flex items-center gap-1 text-[11px] font-semibold text-amber-700"><AlertTriangle className="h-3 w-3" /> Δεν προβλήθηκε</span>
                  )}
                </div>
                <p className="mt-1 text-xs text-slate-500">{SERVICE_LABELS[lead.service] || lead.service} · <span className="inline-flex items-center gap-1"><Clock3 className="h-3 w-3" /> πριν από {ageLabel}</span></p>
              </button>
              <LeadQuickActions phone={lead.phone} email={lead.email} name={lead.name} service={SERVICE_LABELS[lead.service] || lead.service} partnerName={partnerName} compact />
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default TodaysFocus;
